import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { packagingMaterialService, supplierDirectoryService } from '../services/api';
import EntityImportExportButtons from '../components/EntityImportExportButtons';
import PageBackHeader from '../components/PageBackHeader';
import NomenclatureUsageDialog, { checkNomenclatureDelete } from '../components/NomenclatureUsageDialog';

/** Текст помилок з відповіді DRF (поля + detail). */
function formatDrfErrors(data) {
  if (data == null) return null;
  if (typeof data === 'string') return data;
  if (data.detail != null) {
    return Array.isArray(data.detail) ? data.detail.join('\n') : String(data.detail);
  }
  const lines = [];
  Object.keys(data).forEach((key) => {
    const v = data[key];
    if (v == null) return;
    if (Array.isArray(v)) {
      const text = v.join(' ');
      if (key === 'name' || key === 'non_field_errors') {
        lines.push(text);
        return;
      }
      const label = key === 'article' ? 'Артикул' : key;
      lines.push(label ? `${label}: ${text}` : text);
    } else if (typeof v === 'object') {
      lines.push(`${key}: ${JSON.stringify(v)}`);
    } else if (key === 'name') {
      lines.push(String(v));
    } else {
      lines.push(`${key}: ${v}`);
    }
  });
  return lines.length ? lines.join('\n') : JSON.stringify(data);
}

function PackagingMaterialsPage() {
  const [packagingMaterials, setPackagingMaterials] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [mergeCandidate, setMergeCandidate] = useState(null);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [usageDialog, setUsageDialog] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    supplier_id: '',
    supplier_name: '',
    article: '',
    supplier_article: '',
    type: '',
    unit: 'шт',
    description: '',
    minimum_stock: '',
    unit_price: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [packRes, supRes] = await Promise.all([
        packagingMaterialService.getAll(),
        supplierDirectoryService.getAll({ is_active: true }).catch(() => ({ data: [] })),
      ]);
      setPackagingMaterials(packRes.data.results || packRes.data || []);
      const supList = Array.isArray(supRes.data) ? supRes.data : (supRes.data?.results || []);
      setSuppliers(supList.filter((s) => s.is_active !== false));
    } catch (error) {
      console.error('Помилка завантаження:', error);
    } finally {
      setLoading(false);
    }
  };

  const resolveSupplierId = (supplierName) => {
    const n = String(supplierName || '').trim().toLowerCase();
    if (!n) return '';
    const hit = suppliers.find((s) => String(s.name || '').trim().toLowerCase() === n);
    return hit ? String(hit.id) : '';
  };

  const handleOpen = (packagingMaterial = null) => {
    if (packagingMaterial) {
      setEditing(packagingMaterial.id);
      setFormData({
        name: packagingMaterial.name,
        supplier_id: resolveSupplierId(packagingMaterial.supplier_name),
        supplier_name: packagingMaterial.supplier_name || '',
        article: packagingMaterial.article || '',
        supplier_article: packagingMaterial.supplier_article || '',
        type: packagingMaterial.type,
        unit: packagingMaterial.unit === 'м' ? 'м' : 'шт',
        description: packagingMaterial.description || '',
        minimum_stock:
          packagingMaterial.minimum_stock != null && packagingMaterial.minimum_stock !== ''
            ? String(packagingMaterial.minimum_stock)
            : '',
        unit_price:
          packagingMaterial.unit_price != null && packagingMaterial.unit_price !== ''
            ? String(packagingMaterial.unit_price)
            : '',
      });
    } else {
      setEditing(null);
      setFormData({
        name: '',
        supplier_id: '',
        supplier_name: '',
        article: '',
        supplier_article: '',
        type: '',
        unit: 'шт',
        description: '',
        minimum_stock: '',
        unit_price: '',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
  };

  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (saving) return;
    try {
      const upStr = String(formData.unit_price ?? '').trim().replace(',', '.');
      let unit_price = null;
      if (upStr !== '') {
        const n = parseFloat(upStr);
        if (!Number.isFinite(n) || n < 0) {
          alert('Вкажіть коректну базову ціну (невід’ємне число) або залиште поле порожнім');
          return;
        }
        unit_price = n;
      }
      const articleTrimmed = String(formData.article ?? '').trim();
      const articleNorm = articleTrimmed === '' ? null : articleTrimmed;

      const existing =
        editing != null ? packagingMaterials.find((m) => Number(m.id) === Number(editing)) : null;

      if (articleNorm != null) {
        const dup = packagingMaterials.find(
          (m) =>
            String(m.article ?? '').trim() === articleNorm &&
            (!editing || Number(m.id) !== Number(editing)),
        );
        if (dup) {
          alert(
            `Артикул уже використовується в іншій позиції («${dup.name}»). Вкажіть інший артикул або залиште поле порожнім.`,
          );
          return;
        }
      }

      const unit = formData.unit === 'м' ? 'м' : 'шт';
      const selectedSupplier = suppliers.find(
        (s) => String(s.id) === String(formData.supplier_id || ''),
      );
      const supplierName = selectedSupplier
        ? (selectedSupplier.name || '').trim()
        : (formData.supplier_name || '').trim();
      const payload = {
        name: formData.name,
        supplier_name: supplierName,
        article: articleNorm,
        supplier_article: formData.supplier_article || '',
        type: formData.type,
        unit,
        description: formData.description || '',
        unit_price,
        minimum_stock:
          formData.minimum_stock === '' || formData.minimum_stock == null
            ? null
            : Number(formData.minimum_stock),
        ...(existing && existing.quantity != null && existing.quantity !== ''
          ? { quantity: Number(existing.quantity) }
          : {}),
      };
      const nameTrim = (formData.name || '').trim().toLowerCase();
      if (nameTrim) {
        const nameDup = packagingMaterials.find(
          (m) =>
            (m.name || '').trim().toLowerCase() === nameTrim &&
            (!editing || Number(m.id) !== Number(editing)),
        );
        if (nameDup) {
          alert('Така назва вже існує');
          return;
        }
      }
      // Перевірка на злиття (схожі назва/постачальник — не точний дубль назви)
      const supNameTrim = (formData.supplier_name || '').trim().toLowerCase();
      const mergeMatch = packagingMaterials.find((m) => {
        if (editing && Number(m.id) === Number(editing)) return false;
        const mName = (m.name || '').trim().toLowerCase();
        const mSupName = (m.supplier_name || '').trim().toLowerCase();
        return (supNameTrim && mName && supNameTrim === mName) ||
               (nameTrim && mSupName && nameTrim === mSupName);
      });
      if (mergeMatch) {
        setMergeCandidate(mergeMatch);
        setPendingPayload({ payload, editing });
        return;
      }

      setSaving(true);
      if (editing) {
        await packagingMaterialService.update(editing, payload);
      } else {
        await packagingMaterialService.create(payload);
      }
      handleClose();
      loadData();
    } catch (error) {
      console.error('Помилка збереження:', error);
      const msg =
        formatDrfErrors(error.response?.data) ||
        error.response?.statusText ||
        error.message ||
        'Помилка збереження даних';
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSeparate = async () => {
    if (!pendingPayload) return;
    const { payload, editing: ed } = pendingPayload;
    setMergeCandidate(null);
    setPendingPayload(null);
    try {
      if (ed) { await packagingMaterialService.update(ed, payload); }
      else { await packagingMaterialService.create(payload); }
      handleClose();
      loadData();
    } catch (error) {
      const msg = formatDrfErrors(error.response?.data) || error.message || 'Помилка збереження';
      alert(msg);
    }
  };

  const handleMerge = async () => {
    if (!mergeCandidate || !pendingPayload) return;
    const { payload, editing: ed } = pendingPayload;
    setMergeCandidate(null);
    setPendingPayload(null);
    try {
      const merged = {
        ...payload,
        name: payload.name || mergeCandidate.name || '',
        supplier_name: payload.supplier_name || mergeCandidate.supplier_name || '',
        article: payload.article || mergeCandidate.article || null,
        supplier_article: payload.supplier_article || mergeCandidate.supplier_article || '',
        type: payload.type || mergeCandidate.type || '',
        unit: payload.unit || mergeCandidate.unit || 'шт',
        description: payload.description || mergeCandidate.description || '',
        unit_price: payload.unit_price ?? mergeCandidate.unit_price ?? null,
        minimum_stock: payload.minimum_stock ?? mergeCandidate.minimum_stock ?? null,
      };
      if (ed) {
        await packagingMaterialService.update(ed, merged);
        await packagingMaterialService.delete(mergeCandidate.id);
      } else {
        await packagingMaterialService.update(mergeCandidate.id, merged);
      }
      handleClose();
      loadData();
    } catch (error) {
      const msg = formatDrfErrors(error.response?.data) || error.message || 'Помилка злиття';
      alert(msg);
    }
  };

  const handleDelete = async (id) => {
    const item = packagingMaterials.find((x) => x.id === id);
    const itemName = item?.name || '';
    try {
      const check = await checkNomenclatureDelete(packagingMaterialService, id, {
        itemName,
        confirmMessage: 'Ви впевнені, що хочете видалити цей упаковочний матеріал?',
      });
      if (check.blocked) {
        setUsageDialog({ usage: check.usage, itemName: check.itemName || itemName });
        return;
      }
      if (!check.confirmed) return;
      await packagingMaterialService.delete(id);
      loadData();
    } catch (error) {
      const data = error.response?.data;
      if (error.response?.status === 409 && data?.in_use) {
        setUsageDialog({ usage: data, itemName: data.name || itemName });
        return;
      }
      console.error('Помилка видалення:', error);
      alert(formatDrfErrors(data) || 'Помилка видалення');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageBackHeader />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Typography variant="h4">Упаковочні матеріали</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <EntityImportExportButtons entityKey="packaging_materials" label="Упаковка" />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
          >
            Додати упаковку
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Назва</TableCell>
              <TableCell>Постачальник</TableCell>
              <TableCell>Артикул</TableCell>
              <TableCell>Артикул постачальника</TableCell>
              <TableCell>Тип</TableCell>
              <TableCell>Од.</TableCell>
              <TableCell>Базова ціна</TableCell>
              <TableCell>Мін. залишок</TableCell>
              <TableCell>Опис</TableCell>
              <TableCell>Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {packagingMaterials.map((packagingMaterial) => {
              const u = packagingMaterial.unit === 'м' ? 'м' : 'шт';
              return (
              <TableRow key={packagingMaterial.id}>
                <TableCell>{packagingMaterial.name}</TableCell>
                <TableCell>{packagingMaterial.supplier_name || '—'}</TableCell>
                <TableCell>{packagingMaterial.article || '—'}</TableCell>
                <TableCell>{packagingMaterial.supplier_article || '—'}</TableCell>
                <TableCell>{packagingMaterial.type}</TableCell>
                <TableCell>{u}</TableCell>
                <TableCell>
                  {packagingMaterial.unit_price != null
                    ? `${packagingMaterial.unit_price} грн/${u}`
                    : '—'}
                </TableCell>
                <TableCell>
                  {packagingMaterial.minimum_stock != null
                    ? `${packagingMaterial.minimum_stock} ${u}`
                    : '—'}
                </TableCell>
                <TableCell>{packagingMaterial.description || '-'}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpen(packagingMaterial)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(packagingMaterial.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing ? 'Редагувати упаковочний матеріал' : 'Додати упаковочний матеріал'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Назва"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
            />
            <TextField
              select
              label="Постачальник"
              value={formData.supplier_id || ''}
              onChange={(e) => {
                const id = e.target.value;
                const s = suppliers.find((x) => String(x.id) === String(id));
                setFormData({
                  ...formData,
                  supplier_id: id,
                  supplier_name: s ? (s.name || '') : '',
                });
              }}
              fullWidth
              helperText="З довідника постачальників; можна залишити порожнім"
            >
              <MenuItem value="">— без постачальника —</MenuItem>
              {suppliers.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Артикул"
              value={formData.article}
              onChange={(e) => setFormData({ ...formData, article: e.target.value })}
              fullWidth
              helperText="Унікальний серед упаковки; порожньо — без артикула"
            />
            <TextField
              label="Артикул постачальника"
              value={formData.supplier_article}
              onChange={(e) => setFormData({ ...formData, supplier_article: e.target.value })}
              fullWidth
            />
            <TextField
              label="Тип"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              fullWidth
            />
            <TextField
              select
              label="Одиниця виміру"
              value={formData.unit || 'шт'}
              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
              fullWidth
              helperText="Для рулонів оберіть «м» — залишок і ціна будуть у метрах"
            >
              <MenuItem value="шт">шт</MenuItem>
              <MenuItem value="м">м</MenuItem>
            </TextField>
            <TextField
              label={`Базова ціна (грн / ${formData.unit === 'м' ? 'м' : 'шт'})`}
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              value={formData.unit_price}
              onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
              fullWidth
              helperText="Ціна в обраній одиниці; та сама, що в «Довіднику цін матеріалів»"
            />
            <TextField
              label={`Мінімальний залишок (${formData.unit === 'м' ? 'м' : 'шт'})`}
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              value={formData.minimum_stock}
              onChange={(e) => setFormData({ ...formData, minimum_stock: e.target.value })}
              fullWidth
              helperText="Для критичних залишків на головній; у тій самій одиниці"
            />
            <TextField
              label="Опис"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Скасувати</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Діалог злиття */}
      <Dialog open={Boolean(mergeCandidate)} onClose={() => { setMergeCandidate(null); setPendingPayload(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Знайдено схожу позицію</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Назва або назва постачальника збігається з існуючою позицією:
          </Typography>
          <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, my: 1 }}>
            <Typography><b>Назва:</b> {mergeCandidate?.name || '—'}</Typography>
            <Typography><b>Назва постачальника:</b> {mergeCandidate?.supplier_name || '—'}</Typography>
            <Typography><b>Артикул:</b> {mergeCandidate?.article || '—'}</Typography>
            <Typography><b>Артикул постачальника:</b> {mergeCandidate?.supplier_article || '—'}</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Об'єднати в одну картку (пусті поля заповняться з іншої позиції) або зберегти як окремі?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMergeCandidate(null); setPendingPayload(null); }}>Скасувати</Button>
          <Button onClick={handleSaveSeparate} variant="outlined">Зберегти окремо</Button>
          <Button onClick={handleMerge} variant="contained" color="primary">Об'єднати</Button>
        </DialogActions>
      </Dialog>

      <NomenclatureUsageDialog
        open={!!usageDialog}
        onClose={() => setUsageDialog(null)}
        usage={usageDialog?.usage}
        itemName={usageDialog?.itemName}
      />
    </Box>
  );
}

export default PackagingMaterialsPage;
